// WeekViewMobile.jsx
import "./WeekViewMobile.scss";

import { useEffect, useMemo, useRef, useState } from "react";

const isBlank = (v) => v == null || String(v).trim() === "";
const pick = (...vals) => vals.find((v) => !isBlank(v)) ?? "";

// helpers
const toMinutes = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const weekKeyFromDate = (date) => {
  const map = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
  return map[date.getDay()];
};

const parseISODate = (isoDateStr) => {
  if (!isoDateStr) return null;
  const [y, m, d] = String(isoDateStr).split("-").map(Number);
  if ([y, m, d].some((n) => Number.isNaN(n))) return null;
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  dt.setHours(0, 0, 0, 0);
  return dt;
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const minutesToHHmm = (mins) => {
  if (mins == null || !Number.isFinite(mins)) return "";
  const v = clamp(Math.round(mins), 0, 24 * 60 - 1); // 0..1439
  const h = String(Math.floor(v / 60)).padStart(2, "0");
  const m = String(v % 60).padStart(2, "0");
  return `${h}:${m}`;
};

const isDateWithinISOInterval = (date, startISO, endISO) => {
  const start = parseISODate(startISO);
  const end = parseISODate(endISO || startISO);
  if (!start || !end) return false;

  const d = new Date(date);
  d.setHours(0, 0, 0, 0);

  const startMs = start.getTime();
  const endMs = end.getTime();
  const min = Math.min(startMs, endMs);
  const max = Math.max(startMs, endMs);

  return d.getTime() >= min && d.getTime() <= max;
};

const formatISOToBR = (iso) => {
  const d = parseISODate(iso);
  if (!d) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}/${mm}/${yy}`;
};

const closeTypeLabel = (type) => {
  const t = String(type || "").toUpperCase();
  if (t === "VACATION" || t === "FERIAS") return "Férias";
  if (t === "HOLIDAY" || t === "FERIADO") return "Feriado";
  return "Folga";
};

function WeekViewMobile({
  currentDate: initialDate,
  events = [],
  onDayClick,
  eventClick,
  intervalMinutes: intervalFromProps,
  employees = [],
  onlyHoursWorked,
  selectedEmployeeId,
  onEmptySlotClick,
  closeDiary = [],
}) {
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const eventsColRef = useRef(null);

  const intervalMinutes = intervalFromProps ?? 30;
  const slotHeightPx = 60;

  const displayStepMinutes = intervalMinutes === 60 ? 60 : intervalMinutes;
  const pxPerMinute = slotHeightPx / displayStepMinutes;

  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const date = new Date(initialDate);
    const day = date.getDay();
    date.setDate(date.getDate() - day);
    date.setHours(0, 0, 0, 0);
    return date;
  });

  const [selectedDay, setSelectedDay] = useState(initialDate);

  useEffect(() => {
    const d = new Date(initialDate);
    const day = d.getDay();
    const weekStart = new Date(d);
    weekStart.setDate(d.getDate() - day);
    weekStart.setHours(0, 0, 0, 0);

    setCurrentWeekStart(weekStart);
    setSelectedDay(d);
  }, [initialDate]);

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const weekDays = getWeekDays();

  const monthNames = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
  ];

  const floorToStep = (min) => Math.floor(min / displayStepMinutes) * displayStepMinutes;
  const ceilToStep = (min) => Math.ceil(min / displayStepMinutes) * displayStepMinutes;

  const targetEmployee = useMemo(() => {
    if (!employees?.length) return null;
    const id = selectedEmployeeId ?? employees[0]?.id;
    return employees.find((e) => e.id === id) ?? employees[0];
  }, [employees, selectedEmployeeId]);

  const dayEvents = useMemo(() => {
    return events.filter((event) => {
      const eventDate = new Date(event.start);
      return eventDate.toDateString() === selectedDay.toDateString();
    });
  }, [events, selectedDay]);

  /**
   * Normaliza o config do dia (aceita:
   * - targetEmployee.weeksConfigMap[WEEK] (recomendado)
   * - targetEmployee.weeksConfig[] (fallback, com campos novos/antigos)
   */
  const dayWorkCfg = useMemo(() => {
    const weekKey = weekKeyFromDate(selectedDay);

    // 1) weeksConfigMap (vem do Scheduler)
    const mapCfg = targetEmployee?.weeksConfigMap?.[weekKey];
    if (mapCfg) {
      const mStart = pick(mapCfg?.morning?.start, mapCfg?.startTimeMorning);
      const mEnd = pick(mapCfg?.morning?.end, mapCfg?.endTimeMorning, mapCfg?.endTimeTimeMorning);
      const aStart = pick(mapCfg?.afternoon?.start, mapCfg?.startTimeAfternoon);
      const aEnd = pick(mapCfg?.afternoon?.end, mapCfg?.endTimeAfternoon, mapCfg?.endTimeTimeAfternoon);

      return {
        morning: { start: mStart, end: mEnd },
        afternoon: { start: aStart, end: aEnd },
      };
    }

    // 2) fallback: weeksConfig array
    const raw = targetEmployee?.weeksConfig?.find((w) => w.week === weekKey);
    if (!raw) return null;

    // novo
    const mStart = pick(raw.startTimeMorning);
    const mEnd = pick(raw.endTimeMorning, raw.endTimeTimeMorning);
    const aStart = pick(raw.startTimeAfternoon);
    const aEnd = pick(raw.endTimeAfternoon, raw.endTimeTimeAfternoon);

    // antigo (startTime/endTime)
    const legacyStart = pick(raw.startTime);
    const legacyEnd = pick(raw.endTime);

    const hasNewAny = !isBlank(mStart) || !isBlank(mEnd) || !isBlank(aStart) || !isBlank(aEnd);

    if (hasNewAny) {
      return {
        morning: { start: mStart, end: mEnd },
        afternoon: { start: aStart, end: aEnd },
      };
    }

    return {
      morning: { start: legacyStart, end: legacyEnd }, // considera tudo como "manhã"
      afternoon: { start: "", end: "" },
    };
  }, [selectedDay, targetEmployee]);

  const workIntervals = useMemo(() => {
    const arr = [];
    const mS = toMinutes(dayWorkCfg?.morning?.start);
    const mE = toMinutes(dayWorkCfg?.morning?.end);
    const aS = toMinutes(dayWorkCfg?.afternoon?.start);
    const aE = toMinutes(dayWorkCfg?.afternoon?.end);

    if (mS != null && mE != null && mE > mS) {
      arr.push({
        label: "Manhã",
        startMin: mS,
        endMin: mE,
        startTime: dayWorkCfg?.morning?.start,
        endTime: dayWorkCfg?.morning?.end,
      });
    }

    if (aS != null && aE != null && aE > aS) {
      arr.push({
        label: "Tarde",
        startMin: aS,
        endMin: aE,
        startTime: dayWorkCfg?.afternoon?.start,
        endTime: dayWorkCfg?.afternoon?.end,
      });
    }

    return arr.sort((a, b) => a.startMin - b.startMin);
  }, [dayWorkCfg]);

  const { rangeStartMin, rangeEndMin } = useMemo(() => {
    const DAY_START = 0;
    const DAY_END = 24 * 60;

    if (!onlyHoursWorked) return { rangeStartMin: DAY_START, rangeEndMin: DAY_END };

    // base range: do início do primeiro intervalo até o fim do último
    let baseStart = workIntervals?.[0]?.startMin ?? null;
    let baseEnd = workIntervals?.[workIntervals.length - 1]?.endMin ?? null;

    if (baseStart == null || baseEnd == null || baseEnd <= baseStart) {
      baseStart = DAY_START;
      baseEnd = DAY_END;
    }

    const BUFFER_MIN = displayStepMinutes;

    let minEvt = null;
    let maxEvt = null;

    for (const ev of dayEvents) {
      const s = new Date(ev.start);
      const e = new Date(ev.end);
      const sMin = s.getHours() * 60 + s.getMinutes();
      const eMin = e.getHours() * 60 + e.getMinutes();
      if (minEvt == null || sMin < minEvt) minEvt = sMin;
      if (maxEvt == null || eMin > maxEvt) maxEvt = eMin;
    }

    let finalStart = baseStart;
    let finalEnd = baseEnd;

    if (minEvt != null && maxEvt != null) {
      finalStart = Math.min(finalStart, minEvt - BUFFER_MIN);
      finalEnd = Math.max(finalEnd, maxEvt + BUFFER_MIN);
    }

    finalStart = clamp(finalStart, DAY_START, DAY_END);
    finalEnd = clamp(finalEnd, DAY_START, DAY_END);

    finalStart = floorToStep(finalStart);
    finalEnd = ceilToStep(finalEnd);

    if (finalEnd <= finalStart) {
      finalStart = DAY_START;
      finalEnd = DAY_END;
    }

    return { rangeStartMin: finalStart, rangeEndMin: finalEnd };
  }, [onlyHoursWorked, displayStepMinutes, dayEvents, workIntervals]);

  const timeLabels = useMemo(() => {
    const arr = [];
    for (let m = rangeStartMin; m < rangeEndMin; m += displayStepMinutes) {
      const h = Math.floor(m / 60);
      const mm = m % 60;
      arr.push({
        minutes: m,
        label: `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`,
      });
    }
    return arr;
  }, [displayStepMinutes, rangeStartMin, rangeEndMin]);

  const totalHeightPx = (rangeEndMin - rangeStartMin) * pxPerMinute;

  const dayCloseBlocks = useMemo(() => {
    if (!Array.isArray(closeDiary) || closeDiary.length === 0) return [];

    const empId = targetEmployee?.id ?? selectedEmployeeId;
    if (!empId) return [];

    return closeDiary
      .filter((c) => String(c?.employeeId) === String(empId))
      .filter((c) => isDateWithinISOInterval(selectedDay, c?.startDate, c?.endDate))
      .map((c) => {
        const startISO = c?.startDate;
        const endISO = c?.endDate || c?.startDate;

        const startBR = formatISOToBR(startISO);
        const endBR = formatISOToBR(endISO);

        const periodLabel =
          startISO && endISO
            ? startISO === endISO
              ? startBR
              : `${startBR} – ${endBR}`
            : "";

        const startMin = toMinutes(c?.startTime);
        const endMin = toMinutes(c?.endTime);
        const hasRange = startMin != null && endMin != null && endMin > startMin;

        return {
          ...c,
          __label: closeTypeLabel(c?.type),
          __periodLabel: periodLabel,
          __startMin: hasRange ? startMin : 0,
          __endMin: hasRange ? endMin : 24 * 60,
          __hasRange: hasRange,
        };
      });
  }, [closeDiary, selectedDay, targetEmployee, selectedEmployeeId]);

  /**
   * ✅ BLOQUEIOS AUTOMÁTICOS:
   * - Fora do expediente (antes do 1º intervalo / depois do último / entre intervalos quando tiver almoço)
   * - Se não trabalha no dia: bloqueia o dia todo
   */
  const dayWorkBlocks = useMemo(() => {
    const DAY_START = 0;
    const DAY_END = 24 * 60;

    if (!workIntervals?.length) {
      return [
        {
          __label: "Fora do horário de trabalho",
          __startMin: DAY_START,
          __endMin: DAY_END,
          __hasRange: false,
          startTime: "",
          endTime: "",
        },
      ];
    }

    const blocks = [];

    if (onlyHoursWorked) {
      for (let i = 0; i < workIntervals.length - 1; i++) {
        const a = workIntervals[i];
        const b = workIntervals[i + 1];
        if (b.startMin > a.endMin) {
          blocks.push({
            __label: "Intervalo",
            __startMin: a.endMin,
            __endMin: b.startMin,
            __hasRange: true,
            startTime: a.endTime || "—",
            endTime: b.startTime || "—",
          });
        }
      }
      return blocks;
    }

    const first = workIntervals[0];
    const last = workIntervals[workIntervals.length - 1];

    if (first.startMin > DAY_START) {
      blocks.push({
        __label: "Fora do horário de trabalho",
        __startMin: DAY_START,
        __endMin: first.startMin,
        __hasRange: true,
        startTime: "00:00",
        endTime: first.startTime || "—",
      });
    }

    for (let i = 0; i < workIntervals.length - 1; i++) {
      const a = workIntervals[i];
      const b = workIntervals[i + 1];
      if (b.startMin > a.endMin) {
        blocks.push({
          __label: "Intervalo",
          __startMin: a.endMin,
          __endMin: b.startMin,
          __hasRange: true,
          startTime: a.endTime || "—",
          endTime: b.startTime || "—",
        });
      }
    }

    if (last.endMin < DAY_END) {
      blocks.push({
        __label: "Fora do horário de trabalho",
        __startMin: last.endMin,
        __endMin: DAY_END,
        __hasRange: true,
        startTime: last.endTime || "—",
        endTime: "23:59",
      });
    }

    return blocks;
  }, [workIntervals, onlyHoursWorked]);

  const isOverlapping = (eventA, eventB) => {
    const startA = new Date(eventA.start);
    const endA = new Date(eventA.end);
    const startB = new Date(eventB.start);
    const endB = new Date(eventB.end);
    return startA < endB && startB < endA;
  };

  const groupOverlappingEvents = (evts) => {
    const sortedEvents = [...evts].sort((a, b) => new Date(a.start) - new Date(b.start));
    const groups = [];

    sortedEvents.forEach((event) => {
      let placed = false;
      for (const group of groups) {
        if (group.some((e) => isOverlapping(e, event))) {
          group.push(event);
          placed = true;
          break;
        }
      }
      if (!placed) groups.push([event]);
    });

    return groups;
  };

  const isEventSmall = (event) => {
    const start = new Date(event.start);
    const end = new Date(event.end);
    const durationMinutes = (end - start) / (1000 * 60);
    return durationMinutes < 30;
  };

  const eventGroups = groupOverlappingEvents(dayEvents);

  const positionedEvents = [];
  eventGroups.forEach((group) => {
    const hasSmallEvent = group.some(isEventSmall);
    const groupSize = group.length;

    group.forEach((event, index) => {
      if (hasSmallEvent) {
        positionedEvents.push({
          event,
          widthPercent: 100,
          leftPercent: 0,
          colorIndex: index,
        });
      } else {
        positionedEvents.push({
          event,
          widthPercent: 100 / groupSize,
          leftPercent: (100 / groupSize) * index,
          colorIndex: 0,
        });
      }
    });
  });

  const getEventStyle = (event) => {
    const start = new Date(event.start);
    const end = new Date(event.end);

    const startMinutes = start.getHours() * 60 + start.getMinutes();
    const endMinutes = end.getHours() * 60 + end.getMinutes();

    const visibleStart = Math.max(startMinutes, rangeStartMin);
    const visibleEnd = Math.min(endMinutes, rangeEndMin);

    const duration = Math.max(0, visibleEnd - visibleStart);

    const top = (visibleStart - rangeStartMin) * pxPerMinute;
    const height = Math.max(8, duration * pxPerMinute);

    return { top: `${top}px`, height: `${height}px` };
  };

  const getCloseBlockStyle = (block) => {
    const startMin = clamp(block.__startMin, 0, 24 * 60);
    const endMin = clamp(block.__endMin, 0, 24 * 60);

    const visibleStart = clamp(startMin, rangeStartMin, rangeEndMin);
    const visibleEnd = clamp(endMin, rangeStartMin, rangeEndMin);

    const duration = Math.max(0, visibleEnd - visibleStart);
    const top = (visibleStart - rangeStartMin) * pxPerMinute;
    const height = Math.max(14, duration * pxPerMinute);

    return { top: `${top}px`, height: `${height}px` };
  };

  const getCurrentTimePosition = () => {
    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes();
    return (minutes - rangeStartMin) * pxPerMinute;
  };

  const isSelected = (date) => date.toDateString() === selectedDay.toDateString();

  const showCurrentTimeLine = (() => {
    if (!isSelected(new Date())) return false;
    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes();
    return minutes >= rangeStartMin && minutes <= rangeEndMin;
  })();

  useEffect(() => {
    if (!contentRef.current) return;

    if (showCurrentTimeLine) {
      const currentTimePosition = getCurrentTimePosition();
      const scrollPosition = currentTimePosition - 150;

      const t = setTimeout(() => {
        contentRef.current?.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: "smooth",
        });
      }, 80);

      return () => clearTimeout(t);
    }

    const t = setTimeout(() => {
      contentRef.current?.scrollTo({ top: 0, behavior: "auto" });
    }, 0);

    return () => clearTimeout(t);
  }, [
    selectedEmployeeId,
    targetEmployee,
    selectedDay,
    currentWeekStart,
    displayStepMinutes,
    rangeStartMin,
    rangeEndMin,
    showCurrentTimeLine,
  ]);

  const handleEmptySlotClick = (e) => {
    if (e.target.closest(".week-view-mobile__event")) return;
    if (e.target.closest(".week-view-mobile__close-block")) return;

    const col = eventsColRef.current;
    const scroller = contentRef.current;
    if (!col || !scroller) return;

    const rect = col.getBoundingClientRect();
    const clickY = e.clientY - rect.top;

    const yInContent = clickY + (scroller.scrollTop || 0);

    const rawMinutes = rangeStartMin + yInContent / pxPerMinute;

    const snapped = floorToStep(rawMinutes);

    const hhmm = minutesToHHmm(snapped);

    const d = new Date(selectedDay);
    d.setHours(0, 0, 0, 0);

    const [hh, mm] = hhmm.split(":").map(Number);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return;

    d.setHours(hh, mm, 0, 0);

    onEmptySlotClick?.({
      date: d,
      time: hhmm,
      minutes: snapped,
    });
  };

  const isToday = (date) => new Date().toDateString() === date.toDateString();

  const goToPreviousWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() - 7);
    setCurrentWeekStart(newWeekStart);

    const newSelectedDay = new Date(selectedDay);
    newSelectedDay.setDate(newSelectedDay.getDate() - 7);
    setSelectedDay(newSelectedDay);
  };

  const goToNextWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() + 7);
    setCurrentWeekStart(newWeekStart);

    const newSelectedDay = new Date(selectedDay);
    newSelectedDay.setDate(newSelectedDay.getDate() + 7);
    setSelectedDay(newSelectedDay);
  };

  const goToToday = () => {
    const today = new Date();
    const day = today.getDay();
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - day);
    weekStart.setHours(0, 0, 0, 0);

    setCurrentWeekStart(weekStart);
    setSelectedDay(today);
    onDayClick?.(today);
  };

  useEffect(() => {
    const selectedDayIndex = weekDays.findIndex(
      (day) => day.toDateString() === selectedDay.toDateString()
    );
    if (scrollRef.current && selectedDayIndex !== -1) {
      const dayElement = scrollRef.current.children[selectedDayIndex];
      if (dayElement) {
        dayElement.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeekStart]);

  const isCurrentWeek = () => {
    const today = new Date();
    const todayWeekStart = new Date(today);
    const day = today.getDay();
    todayWeekStart.setDate(today.getDate() - day);
    todayWeekStart.setHours(0, 0, 0, 0);
    return currentWeekStart.getTime() === todayWeekStart.getTime();
  };

  const getWeekPeriod = () => {
    const firstDay = weekDays[0];
    const lastDay = weekDays[6];

    if (firstDay.getMonth() === lastDay.getMonth()) {
      return `${firstDay.getDate()} - ${lastDay.getDate()} de ${monthNames[firstDay.getMonth()]}`;
    }
    return `${firstDay.getDate()} de ${monthNames[firstDay.getMonth()]} - ${lastDay.getDate()} de ${
      monthNames[lastDay.getMonth()]
    }`;
  };

  return (
    <div className="week-view-mobile">
      <div className="week-view-mobile__header">
        <div className="week-view-mobile__navigation">
          <button className="week-view-mobile__nav-btn" onClick={goToPreviousWeek}>
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
          </button>

          <div className="week-view-mobile__week-info">
            <span className="week-view-mobile__week-period">{getWeekPeriod()}</span>
            {!isCurrentWeek() && (
              <button className="week-view-mobile__today-btn" onClick={goToToday}>
                Hoje
              </button>
            )}
          </div>

          <button className="week-view-mobile__nav-btn" onClick={goToNextWeek}>
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
            </svg>
          </button>
        </div>

        <div className="week-view-mobile__days" ref={scrollRef}>
          {weekDays.map((day, index) => (
            <DayEventCard
              key={index}
              day={day}
              isToday={isToday(day)}
              isSelected={selectedDay.toDateString() === day.toDateString()}
              onClick={() => {
                setSelectedDay(day);
                onDayClick?.(day);
              }}
            />
          ))}
        </div>
      </div>

      <div
        className="week-view-mobile__content"
        ref={contentRef}
        key={`${selectedEmployeeId ?? "first"}-${intervalMinutes}-${rangeStartMin}-${rangeEndMin}`}
      >
        <div className="week-view-mobile__time-column" style={{ height: `${totalHeightPx}px` }}>
          {timeLabels.map((t) => (
            <div
              key={t.minutes}
              className="week-view-mobile__time-slot"
              style={{ height: `${slotHeightPx}px` }}
            >
              <span className="week-view-mobile__time-label">{t.label}</span>
            </div>
          ))}
        </div>

        <div
          ref={eventsColRef}
          className="week-view-mobile__events-column"
          style={{ height: `${totalHeightPx}px` }}
          onClick={handleEmptySlotClick}
        >
          {timeLabels.map((t) => (
            <div
              key={t.minutes}
              className="week-view-mobile__hour-line"
              style={{ top: `${(t.minutes - rangeStartMin) * pxPerMinute}px` }}
            />
          ))}

          {/* ✅ BLOQUEIO: fora do horário + intervalo (almoço) */}
          {dayWorkBlocks.map((b, idx) => {
            const blockStyle = getCloseBlockStyle(b);
            const h = parseFloat(String(blockStyle.height).replace("px", ""));
            if (!h || h <= 0) return null;

            const isInterval = String(b.__label || "").toLowerCase().includes("intervalo");

            return (
              <div
                key={`workclose-${idx}`}
                className="week-view-mobile__close-block"
                style={{
                  ...blockStyle,
                  position: "absolute",
                  left: 0,
                  width: "100%",
                  zIndex: 1,
                  pointerEvents: "none",
                  opacity: isInterval ? 0.85 : 1,
                }}
              >
                <div className="week-view-mobile__close-block__badge">
                  {isInterval ? "Intervalo •" : "Agenda bloqueada •"} {b.__label}
                  <span className="week-view-mobile__close-block__time">
                    {" "}
                    ({b.__hasRange ? `${b.startTime}–${b.endTime}` : "dia todo"})
                  </span>
                </div>
              </div>
            );
          })}

          {/* Bloqueios do closeDiary */}
          {dayCloseBlocks.map((b, idx) => {
            const blockStyle = getCloseBlockStyle(b);
            const h = parseFloat(String(blockStyle.height).replace("px", ""));
            if (!h || h <= 0) return null;

            return (
              <div
                key={`close-${idx}`}
                className="week-view-mobile__close-block"
                style={{
                  ...blockStyle,
                  position: "absolute",
                  left: 0,
                  width: "100%",
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              >
                <div className="week-view-mobile__close-block__badge">
                  Agenda bloqueada • {b.reason || b.__label}
                  <span className="week-view-mobile__close-block__time">
                    {" "}
                    (
                    {b.__hasRange ? `${b.startTime}–${b.endTime}` : b.__periodLabel || "dia todo"})
                  </span>
                </div>
              </div>
            );
          })}

          {showCurrentTimeLine && (
            <div
              className="week-view-mobile__current-time-line"
              style={{ top: `${getCurrentTimePosition()}px` }}
            >
              <div className="week-view-mobile__current-time-dot" />
              <div className="week-view-mobile__current-time-bar" />
            </div>
          )}

          {positionedEvents.map(({ event, widthPercent, leftPercent, colorIndex }, index) => (
            <div
              key={`wrap-${index}`}
              style={{
                position: "absolute",
                left: `${leftPercent}%`,
                width: `${widthPercent}%`,
                zIndex: 50 + colorIndex,
                boxSizing: "border-box",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                eventClick?.(event);
              }}
            >
              <EventCard
                event={event}
                getEventStyle={getEventStyle}
                style={{
                  position: "absolute",
                  left: 0,
                  width: "100%",
                  zIndex: 50 + colorIndex,
                  boxSizing: "border-box",
                }}
                colorIndex={colorIndex}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DayEventCard({ day, isToday, isSelected, onClick }) {
  return (
    <div
      className={
        `week-view-mobile__day ` +
        (isToday ? "week-view-mobile__day--today " : "") +
        (isSelected ? "week-view-mobile__day--selected " : "")
      }
      onClick={onClick}
    >
      <span className="week-view-mobile__day-name">
        {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"][day.getDay()]}
      </span>
      <span className="week-view-mobile__day-number">{day.getDate()}</span>
    </div>
  );
}

function EventCard({ event, getEventStyle, style, colorIndex = 0 }) {
  const eventRef = useRef(null);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    if (eventRef.current) {
      const height = eventRef.current.offsetHeight;
      setIsCompact(height < 50);
    }
  }, []);

  const now = new Date();
  const start = new Date(event.start);
  const end = new Date(event.end);

  let statusClass = "upcoming";
  if (now >= start && now <= end) statusClass = "in-progress";
  else if (now > end) statusClass = "past";

  const stackedColors = ["#007bff", "#0056b3", "#3399ff", "#66b2ff"];

  const hasCommand = event.commandId != null;
  const isPaid = event.commandStatus === "PAID";

  const isPromotion = Boolean(event?.promotionName);

  // ✅ ASSINATURA / CLUBE
  const hasClub = Boolean(event?.hasClub);

  const backgroundColor =
    style.width === "100%" && !isPaid ? stackedColors[colorIndex % stackedColors.length] : undefined;

  const isInProgress = statusClass === "in-progress";

  return (
    <div
      ref={eventRef}
      className={
        `week-view-mobile__event ` +
        `week-view-mobile__event--${statusClass} ` +
        (isCompact ? "week-view-mobile__event--compact " : "") +
        (hasCommand ? "week-view-mobile__event--has-command " : "") +
        (isPaid ? "week-view-mobile__event--paid " : "") +
        (isInProgress ? "week-view-mobile__event--live " : "") +
        (isPromotion ? "week-view-mobile__event--promotion " : "") +
        (hasClub ? "week-view-mobile__event--club " : "")
      }
      style={{
        ...getEventStyle(event),
        ...style,
        backgroundColor: isInProgress
          ? "#16a34a"
          : isPaid
          ? "#16a34a"
          : backgroundColor || style.backgroundColor || "#667eea",
      }}
      title={hasClub ? "Cliente assinante do clube" : undefined}
    >
      {/* ✅ Badge do CLUBE (aparece sempre que hasClub=true) */}
      {hasClub && (
        <div className="week-view-mobile__event-club-badge" title="Cliente assinante">
          ⭐ Clube
        </div>
      )}

      {isPromotion && (
        <div
          className="week-view-mobile__event-promo-badge"
          title={`Promoção: ${event.promotionName}`}
        >
          🔥 Promo • {event.promotionName}
        </div>
      )}

      {hasCommand && (
        <div
          className={
            "week-view-mobile__event-command-badge " +
            (isPaid ? "week-view-mobile__event-command-badge--paid" : "")
          }
          title={isPaid ? `Comanda #${event.commandId} paga` : `Comanda #${event.commandId}`}
        >
          {isPaid ? "✔ Pago" : "🧾 Comanda"}
        </div>
      )}

      {isCompact ? (
        <div className="week-view-mobile__event-compact-content">
          <span className="week-view-mobile__event-compact-main">
            {event.title}
            {event.client ? ` – ${event.client}` : ""}
            {hasClub ? " • Clube" : ""}
          </span>
        </div>
      ) : (
        <>
          <div className="week-view-mobile__event-title">
            {event.title} - <span className="week-view-mobile__event-client">{event.client}</span>
            {hasClub && <span className="week-view-mobile__event-club-inline"> • Clube</span>}
          </div>
          <div className="week-view-mobile__event-time">
            {start.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} até{" "}
            {end.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
          </div>
        </>
      )}
    </div>
  );
}

export default WeekViewMobile;
